import { useAuth } from '@bb/session-manager';
import { Box, Button, Heading, Text } from '@rocket/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setErrMsg, setSuccessMsg } from '../../redux/actions/snackbarActions';
import { deleteAccount } from '../../requests/subscription';
import { SUBSCRIPTION_UPGRADE_ROUTE } from '../../routes.config';
import { BBDialog } from '../BBDialog/BBDialog';

export function TrialExpiredInterstitial() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { signOut } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (countdown === null) {
      return;
    }
    if (countdown === 0) {
      signOut();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => (c ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, signOut]);

  const handleUpgrade = useCallback(() => {
    navigate({ to: SUBSCRIPTION_UPGRADE_ROUTE });
  }, [navigate]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      dispatch(
        setSuccessMsg(
          'Your account has been marked for deletion. Data will be removed within 30 days.'
        )
      );
      setDeleteDialogOpen(false);
      setCountdown(30);
    } catch {
      dispatch(
        setErrMsg('Failed to process deletion request. Please contact support@blackboiler.com.')
      );
    } finally {
      setDeleting(false);
    }
  }, [dispatch]);

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
      backgroundColor="rgba(0, 0, 0, 0.7)"
      backdropFilter="blur(4px)"
    >
      <Box
        bg="bg"
        borderRadius="12px"
        padding="48px"
        maxWidth="480px"
        width="90%"
        textAlign="center"
        boxShadow="0 24px 48px rgba(0,0,0,0.3)"
      >
        {countdown !== null ? (
          <>
            <Heading as="h2" mb="12px">
              Account deletion requested
            </Heading>
            <Text color="fg.muted" mb="16px">
              Your account has been marked for deletion. Data will be removed within 30 days.
            </Text>
            <Text fontSize="15px" fontWeight="600" color="fg.muted">
              Signing you out in {countdown}…
            </Text>
          </>
        ) : (
          <>
            <Heading as="h1" mb="12px">
              Your trial has ended
            </Heading>
            <Text color="fg.muted" mb="32px">
              Your 7-day free trial has expired. Upgrade to continue using BlackBoiler and keep your
              data.
            </Text>
            <Box display="flex" flexDirection="column" gap="16px">
              <Button colorPalette="blue" size="lg" onClick={handleUpgrade}>
                Upgrade Now
              </Button>
              <Button
                variant="ghost"
                colorPalette="gray"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete my data
              </Button>
            </Box>
          </>
        )}
      </Box>

      <BBDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete your account?"
        size="sm"
        actions={
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', width: '100%' }}>
            <Button
              variant="outline"
              colorPalette="gray"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button colorPalette="red" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? 'Processing...' : 'Delete my data'}
            </Button>
          </div>
        }
      >
        Your data will be permanently deleted within 30 days. This action cannot be undone.
      </BBDialog>
    </Box>
  );
}
